const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtPct = (n, digits = 2) => `${Number(n ?? 0).toFixed(digits)}%`;

export const buildReadinessGaugeStyle = (readiness = 0) => {
  const pct = Math.min(100, Math.max(0, Number(readiness) || 0));
  return {
    background: `radial-gradient(circle at center, #fff 0 52%, transparent 53%), conic-gradient(#f59e0b 0 ${pct}%, rgba(245,158,11,.14) ${pct}% 100%)`,
  };
};

export const buildTeacherReadinessScore = (overview = {}) => {
  const digital = overview.digital_readiness_pct ?? 0;
  const ctet = overview.ctet_coverage_pct ?? 0;
  const cwsn = overview.cwsn_coverage_pct ?? 0;
  const belowGrad = overview.below_grad_pct ?? 0;
  const score =
    digital * 0.55 + ctet * 0.15 + cwsn * 0.15 + (100 - belowGrad) * 0.15;
  return Math.round(Math.min(100, Math.max(0, score)));
};

export const buildReadinessLines = (overview = {}) => [
  {
    label: "Computer Trained",
    value: fmtPct(overview.digital_readiness_pct, 1),
    width: Math.min(100, overview.digital_readiness_pct ?? 0),
    barClass: "green",
  },
  {
    label: "CWSN Trained",
    value: fmtPct(overview.cwsn_coverage_pct, 1),
    width: Math.min(100, overview.cwsn_coverage_pct ?? 0),
    barClass: "purple",
  },
  {
    label: "CTET Coverage",
    value: fmtPct(overview.ctet_coverage_pct, 1),
    width: Math.min(100, overview.ctet_coverage_pct ?? 0),
    barClass: "",
  },
  {
    label: "Below Graduation Risk",
    value: fmtPct(overview.below_grad_pct, 1),
    width: Math.min(100, overview.below_grad_pct ?? 0),
    barClass: "red",
  },
];

export const buildInsightStatusChips = (overview = {}) => {
  const tqi = overview.teacher_quality_index ?? 0;
  const digital = overview.digital_readiness_pct ?? 0;
  const tri = overview.teacher_risk_index ?? 0;
  const chips = [];
  if (tqi < 40) chips.push({ label: "Teacher Quality Is the Constraint", className: "amber" });
  if (digital >= 70) chips.push({ label: "Digital Readiness Is Strong", className: "" });
  if (tri >= 4) chips.push({ label: "TRI Watch Required", className: "" });
  if (!chips.length) chips.push({ label: "Workforce Signals Are Stable", className: "green" });
  return chips;
};

export const buildInsightKpis = (overview = {}) => {
  const growthPct = overview.teacher_growth_pct ?? 0;
  const tqi = overview.teacher_quality_index ?? 0;
  const tri = overview.teacher_risk_index ?? 0;
  return [
    {
      label: "Total Teachers",
      value: fmt(overview.teachers_cy),
      sub: `${growthPct >= 0 ? "+" : ""}${fmtPct(growthPct)} from previous year`,
      iconClass: "",
    },
    {
      label: "Avg Teachers / School",
      value: String(overview.avg_teachers_per_school ?? 0),
      sub: "Capacity base across schools",
      iconClass: "green",
    },
    {
      label: "Quality Index",
      value: Number(tqi).toFixed(1),
      sub: tqi < 40 ? "TQI score needs improvement" : "TQI improving steadily",
      iconClass: "purple",
    },
    {
      label: "Risk Index",
      value: String(tri),
      sub: tri >= 5 ? "TRI watch in selected blocks" : "TRI within moderate range",
      iconClass: "orange",
    },
  ];
};

export const buildPrioritySignals = (overview = {}) => [
  {
    id: "qualification",
    title: "Qualification Depth Is Weak",
    body: `CTET coverage is only ${fmtPct(overview.ctet_coverage_pct)} and ${fmt(overview.below_grad_cy)} teachers are below graduation level. This creates quality risk despite adequate teacher volume.`,
    badge: "High Priority",
    badgeClass: "high",
    iconClass: "purple",
    stats: [
      { value: fmt(overview.ctet_cy), label: "CTET Qualified" },
      { value: fmt(overview.below_grad_cy), label: "Below Graduation" },
    ],
  },
  {
    id: "digital",
    title: "Digital Training Is a Strong Base",
    body: `Computer training coverage is ${fmtPct(overview.digital_readiness_pct)}, giving the district a strong channel to scale refresher learning, dashboards, and digital teaching support.`,
    badge: "Positive Signal",
    badgeClass: "low",
    iconClass: "green",
    stats: [
      { value: fmt(overview.computer_trained_cy), label: "Computer Trained" },
      { value: fmtPct(overview.digital_readiness_pct, 1), label: "Digital Coverage" },
    ],
  },
  {
    id: "deployment",
    title: "Deployment Pressure Is Localized",
    body: "Teacher Risk Index is moderate overall, but deputation and cross-school teaching are concentrated in selected blocks and need targeted monitoring.",
    badge: "Watchlist",
    badgeClass: "medium",
    iconClass: "orange",
    stats: [
      { value: fmt(overview.deputation_cy), label: "On Deputation" },
      { value: fmt(overview.other_school_cy), label: "Cross-School" },
    ],
  },
];

export const buildRootCauseSignals = (overview = {}) => [
  {
    title: "Low Certification Depth",
    body: "Low CTET coverage pulls the quality score down even where staffing strength is sufficient.",
    impact: "High",
    iconClass: "purple",
  },
  {
    title: "Inclusive Education Gap",
    body: `CWSN training is only ${fmtPct(overview.cwsn_coverage_pct)}, creating a capability gap for special needs learners.`,
    impact: "High",
    iconClass: "orange",
  },
  {
    title: "Digital Base Not Yet Converted",
    body: "Strong computer training has not translated into equivalent quality index improvement.",
    impact: "Medium",
    iconClass: "green",
  },
  {
    title: "Block-Level Concentration",
    body: "Risk is not uniform; selected blocks need focused teacher movement and replacement planning.",
    impact: "Medium",
    iconClass: "",
  },
];

export const buildAiNarrativeSummary = (overview = {}) => [
  {
    title: "Teacher Base Is Growing",
    body: `Total teachers increased to ${fmt(overview.teachers_cy)}, but the growth is not enough unless quality coverage improves.`,
  },
  {
    title: "Quality Remains the Leading Constraint",
    body: `The combination of low CTET and CWSN training keeps the TQI at ${Number(overview.teacher_quality_index ?? 0).toFixed(1)}.`,
  },
  {
    title: "Digital Training Can Accelerate Improvement",
    body: `Use the ${fmtPct(overview.digital_readiness_pct)} computer-trained base to deliver micro-learning and compliance refreshers.`,
  },
  {
    title: "Risk Monitoring Should Be Block-Specific",
    body: `TRI at ${overview.teacher_risk_index ?? 0} requires targeted block watchlists rather than broad statewide action.`,
  },
];

export const buildRecommendations = (overview = {}) => {
  const ctetGap = Math.max(0, 100 - (overview.ctet_coverage_pct ?? 0));
  const cwsnGap = Math.max(0, 100 - (overview.cwsn_coverage_pct ?? 0));
  const tri = overview.teacher_risk_index ?? 0;
  const digital = overview.digital_readiness_pct ?? 0;

  const items = [
    {
      step: 1,
      title: "CTET Improvement Sprint",
      body: `Close a ${ctetGap.toFixed(1)} point CTET gap across ${overview.total_schools ?? 0} schools with block-wise certification campaigns.`,
      timeline: ctetGap >= 20 ? "0–30 days" : "30–45 days",
      priority: ctetGap,
    },
    {
      step: 2,
      title: "CWSN Training Cascade",
      body: `Prioritize inclusive education support where CWSN coverage is ${(overview.cwsn_coverage_pct ?? 0).toFixed(1)}%.`,
      timeline: cwsnGap >= 15 ? "30–60 days" : "45–75 days",
      priority: cwsnGap,
    },
    {
      step: 3,
      title: "Teacher Risk Watchlist",
      body: `Monitor deployment pressure while TRI is ${tri} and teacher movement remains ${overview.teacher_growth ?? 0}.`,
      timeline: tri >= 4 ? "Weekly" : "Bi-weekly",
      priority: tri * 10,
    },
    {
      step: 4,
      title: "Digital Refresher Pathway",
      body: `Leverage ${digital.toFixed(1)}% computer-trained coverage for digital pedagogy and assessment refreshers.`,
      timeline: digital >= 50 ? "30–60 days" : "60–90 days",
      priority: digital,
    },
  ];

  return items
    .sort((a, b) => b.priority - a.priority)
    .map((item, index) => ({ ...item, step: index + 1, priority: undefined }));
};

export const buildActionQueue = (overview = {}) => [
  {
    priority: 1,
    theme: "Certification",
    signal: `CTET coverage at ${fmtPct(overview.ctet_coverage_pct)}`,
    owner: "Training Officer",
    due: "30 days",
    status: "Critical",
    statusClass: "red",
  },
  {
    priority: 2,
    theme: "Inclusive Education",
    signal: `CWSN coverage at ${fmtPct(overview.cwsn_coverage_pct)}`,
    owner: "Block Education Officer",
    due: "45 days",
    status: "High",
    statusClass: "amber",
  },
  {
    priority: 3,
    theme: "Deployment",
    signal: `TRI at ${overview.teacher_risk_index ?? 0}`,
    owner: "District HR Cell",
    due: "Weekly",
    status: "Watch",
    statusClass: "amber",
  },
  {
    priority: 4,
    theme: "Digital Enablement",
    signal: `${fmtPct(overview.digital_readiness_pct)} computer trained`,
    owner: "Digital Learning Lead",
    due: "60 days",
    status: "Leverage",
    statusClass: "green",
  },
];

export const buildImpactMatrix = (overview = {}) => {
  const ctetGap = Math.max(0, 100 - (overview.ctet_coverage_pct ?? 0));
  const cwsnGap = Math.max(0, 100 - (overview.cwsn_coverage_pct ?? 0));
  const tri = overview.teacher_risk_index ?? 0;
  const digital = overview.digital_readiness_pct ?? 0;

  return [
    { label: "CTET Sprint", value: Math.min(99, Math.round(70 + ctetGap * 0.22)), barClass: "red" },
    { label: "CWSN Training", value: Math.min(95, Math.round(60 + cwsnGap * 0.24)), barClass: "orange" },
    { label: "Risk Watchlist", value: Math.min(90, Math.round(50 + tri * 5.5)), barClass: "purple" },
    { label: "Digital Refresher", value: Math.min(85, Math.round(40 + digital * 0.34)), barClass: "green" },
    { label: "Block Reviews", value: Math.min(80, Math.round(45 + tri * 3.2)), barClass: "" },
  ];
};

export const buildTeacherInsightsMarkdown = (overview = {}) => {
  const signalLines = buildPrioritySignals(overview).map((s) => `- ${s.title}: ${s.body}`);
  const rootLines = buildRootCauseSignals(overview).map((s) => `- ${s.title}: ${s.body}`);
  const narrativeLines = buildAiNarrativeSummary(overview).map((n) => `- ${n.title}: ${n.body}`);
  const recLines = buildRecommendations(overview).map((r) => `- ${r.title}: ${r.body}`);
  const actionLines = buildActionQueue(overview).map((a) => `- ${a.theme}: ${a.signal}`);

  return [
    "## Insights",
    ...signalLines,
    ...narrativeLines,
    "",
    "## Root Cause Signals",
    ...rootLines,
    "",
    "## Recommendations",
    ...recLines,
    "",
    "## Priority Action Items",
    ...actionLines,
  ].join("\n");
};
